import type { LastOrderTimeCondition as LastOrderTimeConditionType } from "@redotech/redo-model/advanced-flow/rules/schemas";
import { DateQueryOperator } from "@redotech/redo-model/advanced-flow/rules/types";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export const LastOrderTimeCondition = memo(function LastOrderTimeCondition({
  condition,
  onChange,
}: {
  condition: LastOrderTimeConditionType;
  onChange: (updated: LastOrderTimeConditionType) => void;
}) {
  const handleDateChange = (date: Temporal.PlainDate) => {
    onChange({ ...condition, date: date.toString() });
  };

  const handleStartDateChange = (date: Temporal.PlainDate) => {
    onChange({ ...condition, startDate: date.toString() });
  };

  const handleEndDateChange = (date: Temporal.PlainDate) => {
    onChange({ ...condition, endDate: date.toString() });
  };

  const parseDate = (
    dateStr: string | undefined,
  ): Temporal.PlainDate | undefined => {
    if (!dateStr) {
      return undefined;
    }
    try {
      return Temporal.PlainDate.from(dateStr);
    } catch {
      return undefined;
    }
  };

  const handleDaysChange = (value: string) => {
    const days = parseInt(value, 10);
    onChange({ ...condition, days: isNaN(days) ? undefined : days });
  };

  const renderOperatorInputs = () => {
    switch (condition.queryOperator) {
      case DateQueryOperator.WITHIN_LAST:
        return (
          <Flex align="center" gap="sm">
            <RedoTextInput
              inputTypeHint="number"
              min={1}
              placeholder="0"
              setValue={handleDaysChange}
              size="sm"
              value={condition.days?.toString() ?? ""}
            />
            <Text color="secondary" fontSize="sm">
              days
            </Text>
          </Flex>
        );

      case DateQueryOperator.ON_OR_BEFORE:
      case DateQueryOperator.ON_OR_AFTER:
        return (
          <RedoSingleDatePicker
            buttonSize="sm"
            date={parseDate(condition.date)}
            setDate={handleDateChange}
          />
        );

      case DateQueryOperator.BETWEEN:
        return (
          <Flex align="center" gap="sm">
            <RedoSingleDatePicker
              buttonSize="sm"
              date={parseDate(condition.startDate)}
              setDate={handleStartDateChange}
            />
            <Text color="secondary" fontSize="sm">
              and
            </Text>
            <RedoSingleDatePicker
              buttonSize="sm"
              date={parseDate(condition.endDate)}
              setDate={handleEndDateChange}
            />
          </Flex>
        );

      case DateQueryOperator.NEVER:
        return null;

      default:
        return null;
    }
  };

  return (
    <Flex dir="column" gap="md" w="full">
      {renderOperatorInputs()}
    </Flex>
  );
});
